import type { FocusEventHandler } from 'react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslations } from 'next-intl';

import { TextField as Input, TextArea } from '@zealy/design-system';

import { useError } from '../../../../FormError.context';

// allows any object
export const access = (path: string, object: any): string | undefined => {
  return path.split('.').reduce((o, i) => o?.[i], object);
};

export const TextField = ({
  name,
  namespace,
  setOnBlur,
  type,
  textarea,
  trim,
  disabled: disabledProp,
  hint: hintProp,
  ...props
}: {
  name: string;
  namespace: string;
  setOnBlur?: boolean;
  trim?: boolean;
  type: 'textarea' | 'text' | 'number' | undefined;
  textarea?: boolean;
  disabled?: boolean | ((values: Record<string, unknown>) => boolean);
  hint?: string | ((values: Record<string, unknown>) => string | undefined);
}) => {
  const Component = textarea ? TextArea : Input;
  const t = useTranslations('quest.type');
  const {
    setValue,
    watch,
    register,
    formState: { errors },
  } = useFormContext();

  const context = useError(name);

  // Get the parent settings object for conditional props
  const settingsPath = name.replace(/\.settings\..*$/, '.settings');
  const settings = watch(settingsPath);

  // @ts-ignore
  const label = t(`${namespace}.label`);
  // @ts-ignore
  const translatedPlaceholder = t(`${namespace}.placeholder`);
  // @ts-ignore
  const translatedHint = t(`${namespace}.hint`);

  const error = access(`${name}.message`, errors) ?? context.error;

  // Evaluate conditional props
  const disabled = typeof disabledProp === 'function' ? disabledProp(settings) : disabledProp;
  const customHint = typeof hintProp === 'function' ? hintProp(settings) : hintProp;
  const hint = customHint || translatedHint;
  const placeholder = disabled ? '' : translatedPlaceholder;

  const fieldProps = setOnBlur
    ? {
        // We're setting the value onBlur in order to not trigger metadata fetching on every keystroke https://github.com/orgs/react-hook-form/discussions/3620
        onBlur: ((e: React.FocusEvent<HTMLTextAreaElement, Element>) => {
          setValue(name, trim ? e.target.value?.trim() : e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }) as FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>,
        name,
      }
    : register(name, type === 'number' ? { valueAsNumber: true } : {});

  return (
    <Component
      label={label}
      placeholder={placeholder}
      {...props}
      {...(type === 'number' ? { type: 'number' } : {})}
      isInvalid={!!error}
      hint={error ?? hint}
      defaultValue={watch(name)}
      isDisabled={disabled}
      {...fieldProps}
    />
  );
};
